import { useMemo, useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Button, ButtonBase, Grid, Popover, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { useAppContext } from '../../../contexts/AppContext';
import { APP_NAV_ITEMS } from '../../../utils/conts';

export default function AppSelectorPopover() {
  const [anchorEl, setAnchorEl] = useState(null);
  const { selectApp } = useAppContext();

  const apps = useMemo(
    () => [
      {
        key: 'hr',
        label: 'HR',
        icon: 'mdi:account-group-outline',
        color: '#16A34A',
        bg: '#DCFCE7',
      },
      {
        key: 'admin',
        label: 'Admin',
        icon: 'mdi:shield-crown-outline',
        color: '#2563EB',
        bg: '#DBEAFE',
      },
      {
        key: 'finance',
        label: 'Finance',
        icon: 'mdi:calculator-variant-outline',
        color: '#0EA5A4',
        bg: '#CCFBF1',
      },
      {
        key: 'task',
        label: 'Tasks',
        icon: 'mdi:check-circle-outline',
        color: '#9333EA',
        bg: '#F3E8FF',
      },
    ],
    []
  );

  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="outlined"
        color="inherit"
        startIcon={<Iconify icon="mdi:apps" />}
        endIcon={
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              transition: 'transform 140ms ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Iconify icon="eva:chevron-down-fill" />
          </Box>
        }
        sx={(theme) => ({
          height: 34,
          px: 1,
          borderRadius: 1.1,
          borderColor: alpha(theme.palette.grey[500], 0.28),
          color: theme.palette.text.primary,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(10px)',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          '&:hover': {
            borderColor: alpha(theme.palette.grey[500], 0.38),
            bgcolor: alpha(theme.palette.background.paper, 0.9),
          },
          '& .MuiButton-startIcon': { mr: 0.75 },
          '& .MuiButton-endIcon': { ml: 0.5 },
        })}
      >
        Apps
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 1,
              p: 1.5,
              width: 280,
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
              boxShadow: `0 16px 44px ${alpha(theme.palette.common.black, 0.2)}`,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(900px 220px at 10% 0%, ${alpha(
                  theme.palette.primary.light,
                  0.16
                )} 0%, transparent 60%)`,
                pointerEvents: 'none',
              },
            }),
          },
        }}
      >
        <Grid container spacing={1.1} sx={{ position: 'relative' }}>
          {apps.map((app) => (
            <Grid item xs={6} key={app.key}>
              <ButtonBase
                onClick={() => {
                  const firstNavItem = APP_NAV_ITEMS[app.key]?.[0];
                  const firstPagePath = firstNavItem?.children 
                    ? firstNavItem.children[0].path 
                    : firstNavItem?.path;
                  selectApp(app.key, firstPagePath);
                  handleClose();
                }}
                disabled={Boolean(app.disabled)}
                sx={(theme) => ({
                  width: '100%',
                  borderRadius: 1.75,
                  py: 1.2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.9,
                  transition: 'transform 120ms ease, background-color 120ms ease, opacity 120ms ease',
                  bgcolor: 'transparent',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                  '&:active': { transform: 'translateY(0px)' },
                  outline: 'none',
                })}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: app.bg,
                    color: app.color,
                    boxShadow: `0 10px 20px ${alpha(app.color, 0.2)}`,
                  }}
                >
                  <Iconify icon={app.icon} width={24} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.01em',
                    fontSize: '0.8rem',
                    lineHeight: 1.1,
                  }}
                >
                  {app.label}
                </Typography>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Popover>
    </>
  );
}
